import {
  type ReactNode,
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import { Link, useLocation, useNavigate, useParams } from 'react-router-dom';
import { CaremoryMark } from '@/components/icons/caremory-mark';
import clsx from 'clsx';
import { getProfile } from '@/features/profiles/lib/profileQueries';
import { ProfileAvatar } from '@/features/profiles/components/profile-avatar';
import { ProfileSummaryTab } from '@/features/profiles/components/profile-summary-tab';
import { ProfileMedTab } from '@/features/profiles/components/profile-med-tab';
import {
  listAllMedications,
  listTimelineEvents,
  type TimelineEventWithExtras,
} from '@/features/timeline/lib/timelineQueries';
import { TimelineEventCard } from '@/features/timeline/components/timeline-event-card';
import { EventDetail } from '@/features/timeline/components/event-detail';
import { getEventTypeMeta } from '@/features/timeline/lib/eventType';
import {
  calculateAgeVerbose,
  formatMonthKey,
} from '@/features/timeline/lib/formatDate';
import { ShareDoctorModal } from '@/features/share/components/share-doctor-modal';
import { LatestVitalChip } from '@/features/timeline/components/vitals-history';
import type { EventType, Medication, Profile } from '@/types/db';

type ActiveTab = 'timeline' | 'summary' | 'med';

type LoadState =
  | { kind: 'loading' }
  | {
      kind: 'ready';
      profile: Profile;
      events: TimelineEventWithExtras[];
      meds: Medication[];
    }
  | { kind: 'missing' }
  | { kind: 'error'; message: string };

interface FilterOption {
  key: EventType | 'all';
  label: string;
}

const FILTERS: FilterOption[] = [
  { key: 'all', label: 'All' },
  { key: 'prescription', label: 'Prescriptions' },
  { key: 'lab_report', label: 'Labs' },
  { key: 'doctor_visit', label: 'Visits' },
  { key: 'vaccine', label: 'Vaccines' },
  { key: 'vitals', label: 'Vitals' },
];

const TAB_LABELS: Record<ActiveTab, string> = {
  timeline: 'Timeline',
  summary: 'Summary',
  med: 'Med',
};

const TAB_PAGE_LABELS: Record<ActiveTab, string> = {
  timeline: 'Timeline',
  summary: 'Summary',
  med: 'Medications',
};

// ── Small SVG helper (12 px, stroke-based) ────────────────────────────────
function FieldIcon({ children }: { children: ReactNode }) {
  return (
    <svg
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth="1.8"
      strokeLinecap="round"
      strokeLinejoin="round"
      className="h-3 w-3 shrink-0"
      aria-hidden="true"
    >
      {children}
    </svg>
  );
}

interface HealthField {
  label: string;
  value: string;
  icon: ReactNode;
  subLabel?: { text: string; colorClass: string };
}

function calculateBMI(
  weight: number | null,
  height: number | null,
): string | null {
  if (weight == null || height == null || height === 0) return null;
  return (weight / (height / 100) ** 2).toFixed(1);
}

function getBMIStatus(bmi: number): { text: string; colorClass: string } {
  if (bmi < 18.5) return { text: 'Underweight', colorClass: 'text-flag-info' };
  if (bmi < 25) return { text: 'Normal', colorClass: 'text-[#2E7D32]' };
  if (bmi < 30) return { text: 'Overweight', colorClass: 'text-flag-warn' };
  return { text: 'Obese', colorClass: 'text-flag-block' };
}

function buildHealthFields(profile: Profile): HealthField[] {
  const rows: HealthField[] = [];

  if (profile.gender) {
    rows.push({
      label: 'Sex',
      value:
        profile.gender === 'male'
          ? 'Male'
          : profile.gender === 'female'
            ? 'Female'
            : 'Other',
      icon: (
        <FieldIcon>
          <circle cx="12" cy="7" r="4" />
          <path d="M5.5 21c0-3.6 2.9-6.5 6.5-6.5s6.5 2.9 6.5 6.5" />
        </FieldIcon>
      ),
    });
  }
  if (profile.blood_type) {
    rows.push({
      label: 'Blood',
      value: profile.blood_type,
      icon: (
        <FieldIcon>
          <path d="M12 2C8 8 5 11 5 15a7 7 0 0 0 14 0C19 11 16 8 12 2z" />
        </FieldIcon>
      ),
    });
  }
  if (profile.height_cm != null) {
    rows.push({
      label: 'Height',
      value: `${profile.height_cm} cm`,
      icon: (
        <FieldIcon>
          <line x1="12" y1="4" x2="12" y2="20" />
          <polyline points="8 8 12 4 16 8" />
          <polyline points="8 16 12 20 16 16" />
        </FieldIcon>
      ),
    });
  }
  if (profile.weight_kg != null) {
    rows.push({
      label: 'Weight',
      value: `${profile.weight_kg} kg`,
      icon: (
        <FieldIcon>
          <path d="M5 17a7 7 0 0 1 14 0" />
          <path d="M12 10v7" />
          <circle cx="12" cy="10" r="1.5" />
          <line x1="3" y1="21" x2="21" y2="21" />
        </FieldIcon>
      ),
    });
  }
  const bmi = calculateBMI(profile.weight_kg, profile.height_cm);
  if (bmi !== null) {
    const bmiStatus = getBMIStatus(parseFloat(bmi));
    rows.push({
      label: 'BMI',
      value: bmi,
      subLabel: bmiStatus,
      icon: (
        <FieldIcon>
          <line x1="18" y1="20" x2="18" y2="10" />
          <line x1="12" y1="20" x2="12" y2="4" />
          <line x1="6" y1="20" x2="6" y2="14" />
        </FieldIcon>
      ),
    });
  }
  return rows;
}

// ── Tab bar icons ─────────────────────────────────────────────────────────
function TabIcon({ tab }: { tab: ActiveTab | 'ask' }) {
  const cls = 'h-5 w-5';
  const common = {
    viewBox: '0 0 24 24',
    fill: 'none',
    stroke: 'currentColor',
    strokeWidth: 1.8,
    strokeLinecap: 'round' as const,
    strokeLinejoin: 'round' as const,
    className: cls,
    'aria-hidden': true as const,
  };
  if (tab === 'timeline') {
    return (
      <svg {...common}>
        <rect x="3" y="4" width="18" height="18" rx="2" />
        <path d="M16 2v4M8 2v4M3 10h18" />
      </svg>
    );
  }
  if (tab === 'summary') {
    return (
      <svg {...common}>
        <path d="m12 3 2.5 5.5L20 11l-5.5 2.5L12 19l-2.5-5.5L4 11l5.5-2.5L12 3z" />
      </svg>
    );
  }
  if (tab === 'med') {
    return (
      <svg {...common}>
        <path d="M6.5 17.5a5 5 0 0 1 0-7.07l4-4a5 5 0 1 1 7.07 7.07l-4 4a5 5 0 0 1-7.07 0z" />
        <line x1="8.5" y1="8.5" x2="15.5" y2="15.5" />
      </svg>
    );
  }
  // ask
  return (
    <svg {...common}>
      <path d="M21 15a2 2 0 0 1-2 2H7l-4 4V5a2 2 0 0 1 2-2h14a2 2 0 0 1 2 2z" />
    </svg>
  );
}

// ── Shared inline SVGs ────────────────────────────────────────────────────
const BackArrow = () => (
  <svg
    viewBox="0 0 24 24"
    fill="none"
    stroke="currentColor"
    strokeWidth="2"
    strokeLinecap="round"
    strokeLinejoin="round"
    className="h-5 w-5"
    aria-hidden="true"
  >
    <polyline points="15 18 9 12 15 6" />
  </svg>
);

const EditIcon = () => (
  <svg
    viewBox="0 0 24 24"
    fill="none"
    stroke="currentColor"
    strokeWidth="2"
    strokeLinecap="round"
    strokeLinejoin="round"
    className="h-3.5 w-3.5"
    aria-hidden="true"
  >
    <path d="M17 3a2.828 2.828 0 1 1 4 4L7.5 20.5 3 21l.5-4.5z" />
  </svg>
);

const ShareIcon = () => (
  <svg
    viewBox="0 0 24 24"
    fill="none"
    stroke="currentColor"
    strokeWidth="2"
    strokeLinecap="round"
    strokeLinejoin="round"
    className="h-3.5 w-3.5"
    aria-hidden="true"
  >
    <path d="M10 13a5 5 0 0 0 7.54.54l3-3a5 5 0 0 0-7.07-7.07l-1.72 1.71" />
    <path d="M14 11a5 5 0 0 0-7.54-.54l-3 3a5 5 0 0 0 7.07 7.07l1.71-1.71" />
  </svg>
);

export function ProfilePage() {
  const { id } = useParams<{ id: string }>();
  const location = useLocation();
  const navigate = useNavigate();
  const locationState = location.state as {
    justSavedEventId?: string;
    toast?: string;
  } | null;
  const justSavedEventId = locationState?.justSavedEventId ?? null;

  const [state, setState] = useState<LoadState>({ kind: 'loading' });
  const [activeTab, setActiveTab] = useState<ActiveTab>('timeline');
  const [isCompact, setIsCompact] = useState(false);
  const [filter, setFilter] = useState<EventType | 'all'>('all');
  const [openEventId, setOpenEventId] = useState<string | null>(null);
  const [shareOpen, setShareOpen] = useState(false);
  const [toast, setToast] = useState<string | null>(
    justSavedEventId
      ? (locationState?.toast ?? 'Record saved to timeline.')
      : null,
  );
  const highlightRef = useRef<HTMLLIElement | null>(null);
  const scrolledToHighlightRef = useRef(false);

  useEffect(() => {
    const onScroll = () => setIsCompact(window.scrollY > 80);
    window.addEventListener('scroll', onScroll, { passive: true });
    return () => window.removeEventListener('scroll', onScroll);
  }, []);

  useEffect(() => {
    if (justSavedEventId) setActiveTab('timeline');
  }, [justSavedEventId]);

  const load = useCallback(async () => {
    if (!id) return;
    try {
      setState({ kind: 'loading' });
      const [profile, events, meds] = await Promise.all([
        getProfile(id),
        listTimelineEvents(id),
        listAllMedications(id),
      ]);
      if (!profile) {
        setState({ kind: 'missing' });
        return;
      }
      setState({ kind: 'ready', profile, events, meds });
    } catch (err) {
      console.error('[profile] load failed', err);
      setState({ kind: 'error', message: "Couldn't load this profile." });
    }
  }, [id]);

  useEffect(() => {
    load();
  }, [load]);

  useEffect(() => {
    if (!toast) return;
    const t = window.setTimeout(() => setToast(null), 4000);
    return () => window.clearTimeout(t);
  }, [toast]);

  useEffect(() => {
    if (scrolledToHighlightRef.current) return;
    if (state.kind !== 'ready' || !justSavedEventId) return;
    const node = highlightRef.current;
    if (!node) return;
    node.scrollIntoView({ block: 'center', behavior: 'smooth' });
    scrolledToHighlightRef.current = true;
  }, [state, justSavedEventId]);

  const filteredEvents = useMemo(() => {
    if (state.kind !== 'ready') return [];
    if (filter === 'all') return state.events;
    return state.events.filter((e) => e.event_type === filter);
  }, [state, filter]);

  const groupedEvents = useMemo(() => {
    const groups: {
      key: string;
      label: string;
      events: TimelineEventWithExtras[];
    }[] = [];
    for (const event of filteredEvents) {
      const label = formatMonthKey(event.event_date);
      const last = groups[groups.length - 1];
      if (last && last.label === label) {
        last.events.push(event);
      } else {
        groups.push({ key: `${label}-${groups.length}`, label, events: [event] });
      }
    }
    return groups;
  }, [filteredEvents]);

  const openEvent =
    state.kind === 'ready' && openEventId
      ? state.events.find((e) => e.id === openEventId) ?? null
      : null;

  const ageVerbose =
    state.kind === 'ready'
      ? calculateAgeVerbose(state.profile.date_of_birth)
      : null;
  const relation = state.kind === 'ready' ? state.profile.relation : null;
  const relOther =
    state.kind === 'ready' ? state.profile.relation_other : null;
  const relationLabel =
    relation === 'self'
      ? 'You'
      : relation === 'other'
        ? (relOther?.trim() || 'Other')
        : relation
          ? relation.charAt(0).toUpperCase() + relation.slice(1)
          : null;

  const relAge = [relationLabel, ageVerbose].filter(Boolean).join(' · ');
  const activeMedCount =
    state.kind === 'ready'
      ? state.meds.filter((m) => !m.is_stopped).length
      : 0;
  const healthFields =
    state.kind === 'ready' ? buildHealthFields(state.profile) : [];

  return (
    <main className="min-h-screen safe-top pb-28">
      {/* ── Compact sticky bar ─────────────────────────────────────────── */}
      <header
        className={clsx(
          'sticky top-0 z-10 overflow-hidden bg-surface/95 backdrop-blur border-b border-border transition-[height,opacity] duration-200',
          isCompact && state.kind === 'ready'
            ? 'h-[52px]'
            : 'h-0 opacity-0 pointer-events-none',
        )}
      >
        {state.kind === 'ready' && (
          <div className="max-w-md mx-auto px-4 h-[52px] flex items-center gap-2">
            <Link
              to="/home"
              aria-label="Back to family"
              className="h-9 w-9 -ml-1 rounded-full text-ink-muted hover:text-ink active:bg-canvas flex items-center justify-center shrink-0"
            >
              <BackArrow />
            </Link>
            <ProfileAvatar
              name={state.profile.name}
              color={state.profile.avatar_color}
              size="sm"
            />
            <span className="font-semibold text-sm flex-1 truncate">
              {state.profile.name}
            </span>
            {state.profile.allergies.length > 0 && (
              <span
                aria-label="Has allergies"
                className="h-2.5 w-2.5 rounded-full bg-flag-warn shrink-0"
              />
            )}
            <Link
              to={`/profile/${id}/edit`}
              aria-label="Edit profile"
              className="h-9 w-9 rounded-full text-ink-muted hover:text-ink active:bg-canvas flex items-center justify-center shrink-0"
            >
              <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" className="h-4 w-4" aria-hidden="true">
                <path d="M17 3a2.828 2.828 0 1 1 4 4L7.5 20.5 3 21l.5-4.5z" />
              </svg>
            </Link>
            <button
              type="button"
              onClick={() => setShareOpen(true)}
              aria-label="Share with doctor"
              className="h-9 w-9 rounded-full text-evt-rx-ink active:bg-evt-rx-soft flex items-center justify-center shrink-0"
            >
              <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" className="h-4 w-4" aria-hidden="true">
                <path d="M10 13a5 5 0 0 0 7.54.54l3-3a5 5 0 0 0-7.07-7.07l-1.72 1.71" />
                <path d="M14 11a5 5 0 0 0-7.54-.54l-3 3a5 5 0 0 0 7.07 7.07l1.71-1.71" />
              </svg>
            </button>
          </div>
        )}
      </header>

      {/* ── Profile summary card ───────────────────────────────────────── */}
      <div className="px-4 pt-4 max-w-md mx-auto">
        {/* Back arrow — outside the card */}
        <div className="flex items-center mb-2">
          <Link
            to="/home"
            aria-label="Back to family"
            className="h-11 w-11 -ml-2.5 rounded-full text-ink-muted hover:text-ink active:bg-canvas flex items-center justify-center shrink-0"
          >
            <BackArrow />
          </Link>
          {state.kind === 'ready' && (
            <>
              <span className="font-bold text-base text-ink truncate flex-1 ml-1">
                {TAB_PAGE_LABELS[activeTab]}
              </span>
              <CaremoryMark
                variant="icon"
                className="h-7 w-7 shrink-0"
              />
            </>
          )}
        </div>

        {/* Floating card */}
        <div className="card p-4">
          {state.kind === 'loading' ? (
            <div className="h-16 animate-pulse bg-canvas rounded-xl" aria-busy="true" />
          ) : state.kind === 'ready' ? (
            <>
              {/* Avatar + name + action chips */}
              <div className="flex items-center gap-3">
                <ProfileAvatar
                  name={state.profile.name}
                  color={state.profile.avatar_color}
                  size="md"
                />
                <div className="min-w-0 flex-1">
                  <h1 className="font-bold text-base truncate leading-tight">
                    {state.profile.name}
                  </h1>
                  {relAge && (
                    <p className="text-xs text-ink-muted truncate mt-0.5">
                      {relAge}
                    </p>
                  )}
                </div>
                <Link
                  to={`/profile/${id}/edit`}
                  aria-label="Edit profile"
                  className="min-h-[44px] px-3 inline-flex items-center gap-1.5 text-sm font-semibold rounded-full border border-border text-ink-muted hover:text-ink hover:border-border-strong active:bg-canvas shrink-0"
                >
                  <EditIcon />
                  Edit
                </Link>
                <button
                  type="button"
                  onClick={() => setShareOpen(true)}
                  aria-label="Share with doctor"
                  className="min-h-[44px] px-3 inline-flex items-center gap-1.5 text-sm font-semibold rounded-full bg-evt-rx-soft border border-evt-rx/20 text-evt-rx-ink hover:bg-evt-rx/10 active:bg-evt-rx-soft shrink-0"
                >
                  <ShareIcon />
                  Share
                </button>
              </div>

              {/* Health fields grid + vitals chip */}
              {(healthFields.length > 0 || state.kind === 'ready') && (
                <div className="grid grid-cols-3 gap-2 mt-4 pt-3 border-t border-border">
                  {healthFields.map(({ label, value, icon, subLabel }) => (
                    <div key={label} className="flex flex-col bg-surface rounded-[14px] border border-border px-3 py-2 min-h-[56px]">
                      <p className="flex items-center gap-1 text-[10px] uppercase tracking-wider text-ink-subtle font-semibold mb-0.5">
                        {icon}
                        {label}
                      </p>
                      <p className="text-sm font-semibold text-ink">{value}</p>
                      {subLabel && (
                        <p className={`text-[10px] font-semibold mt-0.5 ${subLabel.colorClass}`}>{subLabel.text}</p>
                      )}
                    </div>
                  ))}
                  <LatestVitalChip
                    profileId={state.profile.id}
                    onViewAll={() => navigate(`/profile/${id}/vitals`)}
                  />
                </div>
              )}

              {/* Active medications row */}
              {activeMedCount > 0 && (
                <div className="border-t border-border mt-3 pt-1">
                  <button
                    type="button"
                    onClick={() => setActiveTab('med')}
                    className="w-full flex items-center gap-2.5 min-h-[44px] rounded-xl px-1 hover:bg-canvas active:bg-canvas"
                  >
                    <svg
                      viewBox="0 0 24 24"
                      fill="none"
                      stroke="currentColor"
                      strokeWidth="1.8"
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      className="h-4 w-4 shrink-0 text-[#4DA575]"
                      aria-hidden="true"
                    >
                      <path d="M6.5 17.5a5 5 0 0 1 0-7.07l4-4a5 5 0 1 1 7.07 7.07l-4 4a5 5 0 0 1-7.07 0z" />
                      <line x1="8.5" y1="8.5" x2="15.5" y2="15.5" />
                    </svg>
                    <span className="flex-1 text-sm font-medium text-ink text-left">
                      {activeMedCount} active{' '}
                      {activeMedCount === 1 ? 'medication' : 'medications'}
                    </span>
                    <svg
                      viewBox="0 0 24 24"
                      fill="none"
                      stroke="currentColor"
                      strokeWidth="2"
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      className="h-4 w-4 shrink-0 text-ink-subtle"
                      aria-hidden="true"
                    >
                      <polyline points="9 18 15 12 9 6" />
                    </svg>
                  </button>
                </div>
              )}

              {/* Allergies */}
              {state.profile.allergies.length > 0 && (
                <div
                  className="border-t border-border mt-2 pt-3 flex items-center gap-1.5 flex-wrap"
                  aria-label={`Allergies: ${state.profile.allergies.join(', ')}`}
                >
                  <span
                    aria-hidden="true"
                    className="inline-flex items-center gap-1 text-[10px] font-semibold uppercase tracking-wide text-flag-warn"
                  >
                    <svg
                      viewBox="0 0 24 24"
                      fill="none"
                      stroke="currentColor"
                      strokeWidth="2"
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      className="h-3 w-3"
                    >
                      <path d="M10.29 3.86 1.82 18a2 2 0 0 0 1.71 3h16.94a2 2 0 0 0 1.71-3L13.71 3.86a2 2 0 0 0-3.42 0z" />
                      <line x1="12" y1="9" x2="12" y2="13" />
                      <line x1="12" y1="17" x2="12.01" y2="17" />
                    </svg>
                    Allergies
                  </span>
                  {state.profile.allergies.map((a) => (
                    <span
                      key={a}
                      className="inline-flex items-center px-2 py-0.5 rounded-full text-[11px] font-medium bg-amber-50 text-flag-warn border border-flag-warn/30"
                    >
                      {a}
                    </span>
                  ))}
                </div>
              )}
            </>
          ) : (
            <span className="font-semibold text-ink-muted text-sm">Profile</span>
          )}
        </div>
      </div>

      {/* ── Tab content ──────────────────────────────────────────────────── */}
      <section className="px-4 py-5 max-w-md mx-auto">
        {state.kind === 'missing' && (
          <div className="card p-6 text-center">
            <p className="font-medium mb-2">Profile not found</p>
            <Link to="/home" className="btn-secondary inline-flex">
              Back to family
            </Link>
          </div>
        )}

        {state.kind === 'error' && (
          <div className="card p-6 text-center">
            <p className="text-flag-block mb-3">{state.message}</p>
            <button type="button" onClick={load} className="btn-secondary">
              Try again
            </button>
          </div>
        )}

        {state.kind === 'ready' && (
          <>
            {/* ── Timeline ── */}
            {activeTab === 'timeline' && (
              <>
                <div className="flex gap-2 overflow-x-auto -mx-1 px-1 mb-4 pb-1">
                  {FILTERS.map((f) => {
                    const meta =
                      f.key === 'all' ? null : getEventTypeMeta(f.key);
                    const active = filter === f.key;
                    return (
                      <button
                        key={f.key}
                        type="button"
                        onClick={() => setFilter(f.key)}
                        className={clsx(
                          'shrink-0 min-h-tap px-3 rounded-full border text-sm font-semibold inline-flex items-center gap-1.5',
                          active
                            ? meta
                              ? clsx(
                                  meta.pillBg,
                                  meta.pillText,
                                  'border-transparent',
                                )
                              : 'bg-accent text-white border-accent'
                            : 'bg-surface text-ink-muted border-border',
                        )}
                      >
                        {meta ? (
                          <meta.Icon className="h-3.5 w-3.5 shrink-0" aria-hidden="true" />
                        ) : (
                          <svg
                            viewBox="0 0 16 16"
                            fill="currentColor"
                            className="h-3.5 w-3.5 shrink-0"
                            aria-hidden="true"
                          >
                            <circle cx="4" cy="4" r="1.5" />
                            <circle cx="12" cy="4" r="1.5" />
                            <circle cx="4" cy="12" r="1.5" />
                            <circle cx="12" cy="12" r="1.5" />
                          </svg>
                        )}
                        {f.label}
                      </button>
                    );
                  })}
                </div>

                {state.events.length === 0 ? (
                  <div className="card p-6 text-center">
                    <p className="font-medium mb-1">No records yet</p>
                    <p className="text-sm text-ink-muted mb-4">
                      Take a photo of a prescription to start this timeline.
                    </p>
                    <Link
                      to={`/profile/${state.profile.id}/add`}
                      className="btn-primary inline-flex"
                    >
                      Add first record
                    </Link>
                  </div>
                ) : filteredEvents.length === 0 ? (
                  <div className="card p-6 text-center">
                    <p className="text-sm text-ink-muted">
                      Nothing in this category yet.
                    </p>
                  </div>
                ) : (
                  <div className="relative">
                    <span
                      aria-hidden="true"
                      className="absolute left-9 top-3 bottom-3 w-px bg-border"
                    />
                    {groupedEvents.map((group) => (
                      <section key={group.key} className="mb-5 last:mb-0">
                        <h3 className="pl-12 -ml-1 mb-2 text-[11px] font-semibold uppercase tracking-wider text-ink-muted">
                          {group.label}
                        </h3>
                        <ol className="space-y-3">
                          {group.events.map((event) => {
                            const isHighlight = event.id === justSavedEventId;
                            return (
                              <li
                                key={event.id}
                                ref={isHighlight ? highlightRef : undefined}
                                className="relative"
                              >
                                <TimelineEventCard
                                  event={event}
                                  highlight={isHighlight}
                                  onClick={() => setOpenEventId(event.id)}
                                />
                              </li>
                            );
                          })}
                        </ol>
                      </section>
                    ))}
                  </div>
                )}
              </>
            )}

            {/* ── Summary ── */}
            {activeTab === 'summary' && (
              <ProfileSummaryTab
                profileId={state.profile.id}
                profileName={state.profile.name}
                eventCount={state.events.length}
                latestEventDate={state.events[0]?.created_at ?? null}
                onCitationClick={(eventId) => {
                  setActiveTab('timeline');
                  setOpenEventId(eventId);
                }}
              />
            )}

            {/* ── Med ── */}
            {activeTab === 'med' && (
              <ProfileMedTab
                medications={state.meds}
                onOpenEvent={setOpenEventId}
              />
            )}
          </>
        )}
      </section>

      {/* ── Bottom floating action bar ───────────────────────────────────── */}
      {state.kind === 'ready' && (
        <div
          className="fixed inset-x-0 bottom-0 z-20 pointer-events-none"
          style={{ paddingBottom: 'env(safe-area-inset-bottom)' }}
        >
          <div className="mx-auto max-w-md px-4 pb-3 pointer-events-none">
            <div className="pointer-events-auto flex items-center gap-3">
              <div className="flex-1 flex items-stretch rounded-full bg-surface border border-border shadow-pop overflow-hidden py-1">
                {(Object.keys(TAB_LABELS) as ActiveTab[]).map((tab) => (
                  <button
                    key={tab}
                    type="button"
                    onClick={() => setActiveTab(tab)}
                    aria-selected={activeTab === tab}
                    className={clsx(
                      'flex-1 min-h-tap flex flex-col items-center justify-center gap-0.5 py-1.5 active:bg-canvas',
                      activeTab === tab ? 'text-accent' : 'text-ink-muted',
                    )}
                  >
                    <TabIcon tab={tab} />
                    <span className="text-[10px] font-semibold tracking-wide">
                      {TAB_LABELS[tab]}
                    </span>
                  </button>
                ))}
                <Link
                  to={`/profile/${state.profile.id}/chat`}
                  className="flex-1 min-h-tap flex flex-col items-center justify-center gap-0.5 py-1.5 text-ink-muted active:bg-canvas"
                >
                  <TabIcon tab="ask" />
                  <span className="text-[10px] font-semibold tracking-wide">
                    Ask AI
                  </span>
                </Link>
              </div>
              <Link
                to={`/profile/${state.profile.id}/add`}
                aria-label="Add record"
                className="h-14 w-14 rounded-full bg-accent text-white font-semibold shadow-fab flex items-center justify-center active:bg-accent-hover shrink-0"
              >
                <svg
                  className="h-6 w-6"
                  viewBox="0 0 24 24"
                  fill="none"
                  stroke="currentColor"
                  strokeWidth="2.2"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  aria-hidden="true"
                >
                  <line x1="12" y1="5" x2="12" y2="19" />
                  <line x1="5" y1="12" x2="19" y2="12" />
                </svg>
              </Link>
            </div>
          </div>
        </div>
      )}

      {openEvent && (
        <EventDetail
          event={openEvent}
          onClose={() => setOpenEventId(null)}
          onEdit={() => {
            setOpenEventId(null);
            navigate(`/profile/${id}/event/${openEvent.id}/edit`);
          }}
        />
      )}

      {state.kind === 'ready' && shareOpen && (
        <ShareDoctorModal
          profileId={state.profile.id}
          profileName={state.profile.name}
          onClose={() => setShareOpen(false)}
        />
      )}

      {toast && (
        <div
          role="status"
          className="fixed top-4 left-1/2 -translate-x-1/2 bg-accent text-white px-4 py-2 rounded-full shadow-pop text-sm z-50"
        >
          {toast}
        </div>
      )}
    </main>
  );
}
